import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

const API_BASE = '/api/v1';

// Helper to retrieve the CSRF token cookie value
function getCsrfToken() {
  const match = document.cookie.match(new RegExp('(^| )XSRF-TOKEN=([^;]*)'));
  return match ? decodeURIComponent(match[2]) : null;
}

// Global fetch interceptor for stateful Sanctum session auth
const originalFetch = window.fetch;
window.fetch = async function (url, options = {}) {
  const isTarget = typeof url === 'string' && (url.includes('/api/v1/admin') || url.includes('/sanctum/'));
  
  if (isTarget) {
    options = options || {};
    options.headers = options.headers || {};
    options.credentials = 'include';

    // Remove bearer token headers from client requests
    if (options.headers['Authorization']) {
      delete options.headers['Authorization'];
    }

    // Attach CSRF token
    const csrf = getCsrfToken();
    if (csrf) {
      options.headers['X-XSRF-TOKEN'] = csrf;
    }
  }

  const response = await originalFetch(url, options);

  // Global handle for session expiration (401 or 419)
  if (isTarget && (response.status === 401 || response.status === 419)) {
    if (!url.includes('/admin/me') && !url.includes('/admin/login')) {
      window.location.href = '/admin/login';
    }
  }

  return response;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Provide a dummy token value for legacy client context matching
  const token = isAuthenticated ? 'stateful-session-cookie' : null;

  useEffect(() => {
    // Only probe admin session if user is on an admin route or previously authenticated
    const isAdminRoute = window.location.pathname.startsWith('/admin');
    const hasAdminSession = sessionStorage.getItem('pbz_admin_active') === 'true';

    if (!isAdminRoute && !hasAdminSession) {
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    // Verify session status on mount for admin users
    fetch(`${API_BASE}/admin/me`)
      .then(res => {
        if (res.ok) return res.json();
        throw new Error('Unauthenticated');
      })
      .then(data => {
        setUser(data);
        setIsAuthenticated(true);
        sessionStorage.setItem('pbz_admin_active', 'true');
        setMustChangePassword(!!data.must_change_password);
        setLoading(false);
      })
      .catch(() => {
        setUser(null);
        setIsAuthenticated(false);
        sessionStorage.removeItem('pbz_admin_active');
        setLoading(false);
      });
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    try {
      // 1. Initialize CSRF protection cookie
      await fetch('/sanctum/csrf-cookie');

      // 2. Perform Login POST
      const response = await fetch(`${API_BASE}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      // 3. Fetch Profile details
      const meResponse = await fetch(`${API_BASE}/admin/me`);
      const meData = await meResponse.json();

      setUser(meData);
      setIsAuthenticated(true);
      setMustChangePassword(!!meData.must_change_password);
      setLoading(false);
      return { success: true, mustChangePassword: !!meData.must_change_password };
    } catch (err) {
      setLoading(false);
      return { success: false, error: err.message };
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE}/admin/logout`, { method: 'POST' });
    } catch (e) {
      // Ignore API fail on logout and clear locally
    }
    setUser(null);
    setIsAuthenticated(false);
    setMustChangePassword(false);
  };

  const changePassword = async (password, password_confirmation) => {
    try {
      const response = await fetch(`${API_BASE}/admin/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, password_confirmation })
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.errors) {
          const errors = Object.values(data.errors).flat().join(', ');
          throw new Error(errors);
        }
        throw new Error(data.message || 'Password update failed');
      }

      setMustChangePassword(false);
      if (user) {
        setUser({ ...user, must_change_password: false });
      }
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  return (
    <AuthContext.Provider value={{ token, user, loading, login, logout, changePassword, mustChangePassword, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
